import { useEffect, useRef, useState } from 'react';
import type { CoworkUserError } from '../../../shared/coworkUserError';
import { relayService } from '../../services/relay';

interface ErrorReportStatus {
  errorId: string;
  state: 'pending' | 'failed' | 'reported' | 'disabled' | 'not_found';
  occurrenceId?: number;
  lastError?: string;
}

interface CoworkErrorReportControlProps {
  userError: CoworkUserError;
}

const FEEDBACK_REQUEST_TIMEOUT_MS = 15_000;

/** formatSupportReference 压缩界面中的异常编号，复制时仍保留完整值。 */
const formatSupportReference = (reference: string): string => {
  if (reference.length <= 24) return reference;
  return `${reference.slice(0, 12)}…${reference.slice(-5)}`;
};

/** withFeedbackTimeout 防止API或Relay请求无响应时按钮永久停留在提交中。 */
const withFeedbackTimeout = async <T,>(request: Promise<T>): Promise<T> => {
  let timeout: number | null = null;
  try {
    return await Promise.race([
      request,
      new Promise<T>((_resolve, reject) => {
        timeout = window.setTimeout(() => reject(new Error('反馈提交请求超时，请稍后重试')), FEEDBACK_REQUEST_TIMEOUT_MS);
      }),
    ]);
  } finally {
    if (timeout !== null) window.clearTimeout(timeout);
  }
};

/** CoworkErrorReportControl 提供用户主动提交反馈入口，不暴露后台自动上报状态。 */
const CoworkErrorReportControl = ({ userError }: CoworkErrorReportControlProps) => {
  const [submitting, setSubmitting] = useState(false);
  const [awaitingCompletion, setAwaitingCompletion] = useState(false);
  const [feedbackConfirmed, setFeedbackConfirmed] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [referenceCopied, setReferenceCopied] = useState(false);
  const copyResetTimer = useRef<number | null>(null);
  const feedbackResetTimer = useRef<number | null>(null);
  const submitRequested = useRef(false);
  const relayClientId = userError.runtime?.kind === 'relay_client'
    ? userError.runtime.clientId
    : undefined;

  /** confirmFeedback 短暂展示手工反馈成功状态。 */
  const confirmFeedback = (): void => {
    submitRequested.current = false;
    setSubmitting(false);
    setAwaitingCompletion(false);
    setFeedbackError(null);
    setFeedbackConfirmed(true);
    if (feedbackResetTimer.current !== null) window.clearTimeout(feedbackResetTimer.current);
    feedbackResetTimer.current = window.setTimeout(() => setFeedbackConfirmed(false), 1800);
  };

  /** rejectFeedback 短暂展示手工反馈失败状态。 */
  const rejectFeedback = (message: string): void => {
    submitRequested.current = false;
    setSubmitting(false);
    setAwaitingCompletion(false);
    setFeedbackConfirmed(false);
    setFeedbackError(message);
    if (feedbackResetTimer.current !== null) window.clearTimeout(feedbackResetTimer.current);
    feedbackResetTimer.current = window.setTimeout(() => setFeedbackError(null), 2400);
  };

  /** readStatus 根据异常来源从CE服务端或桌面Relay读取状态。 */
  const readStatus = async (): Promise<ErrorReportStatus> => {
    if (!relayClientId) {
      const result = await window.electron.cowork.getErrorReportStatus(userError.errorId);
      if (result.success && result.status) return result.status;
      throw new Error(result.error ?? '无法查询异常上报状态');
    }
    await relayService.ensureConnected();
    const result = await relayService.getErrorReportStatus(relayClientId, userError.errorId);
    if (result?.status) return result.status as ErrorReportStatus;
    throw new Error('桌面客户端未返回异常上报状态');
  };

  useEffect(() => {
    if (!awaitingCompletion) return;
    let active = true;
    /** settleStatus 仅处理用户本次提交触发的终态，后台状态变化不展示。 */
    const settleStatus = (nextStatus: ErrorReportStatus): void => {
      if (!active || !submitRequested.current) return;
      if (nextStatus.state === 'reported') {
        confirmFeedback();
        return;
      }
      if (['failed', 'disabled', 'not_found'].includes(nextStatus.state)) {
        rejectFeedback(nextStatus.lastError ?? '反馈提交失败，请稍后重试');
      }
    };
    const refresh = async (): Promise<void> => {
      try {
        const nextStatus = await readStatus();
        settleStatus(nextStatus);
      } catch (error) {
        if (active && submitRequested.current) {
          rejectFeedback(relayClientId
            ? '客户端离线，无法立即提交反馈'
            : error instanceof Error ? error.message : '反馈提交失败，请稍后重试');
        }
      }
    };
    const pollTimer = window.setInterval(() => {
      void refresh();
    }, 1000);
    const timeout = window.setTimeout(() => {
      if (active && submitRequested.current) {
        rejectFeedback('暂未提交成功，反馈已保留在来源端，可稍后再次提交');
      }
    }, 30_000);
    const unsubscribe = relayClientId
      ? () => undefined
      : window.electron.cowork.onErrorReportStatusChanged((nextStatus) => {
          if (nextStatus.errorId !== userError.errorId) return;
          settleStatus(nextStatus);
        });
    return () => {
      active = false;
      window.clearInterval(pollTimer);
      window.clearTimeout(timeout);
      unsubscribe();
    };
  }, [awaitingCompletion, relayClientId, userError.errorId]);

  useEffect(() => () => {
    submitRequested.current = false;
    if (copyResetTimer.current !== null) window.clearTimeout(copyResetTimer.current);
    if (feedbackResetTimer.current !== null) window.clearTimeout(feedbackResetTimer.current);
  }, []);

  /** submitFeedback 核验来源端状态，仅对未完成记录触发手工补报。 */
  const submitFeedback = async (): Promise<void> => {
    if (submitRequested.current) return;
    submitRequested.current = true;
    setSubmitting(true);
    try {
      const currentStatus = await withFeedbackTimeout(readStatus());
      if (currentStatus.state === 'reported') {
        confirmFeedback();
        return;
      }
      if (currentStatus.state === 'disabled') {
        rejectFeedback(currentStatus.lastError ?? '平台暂未开放异常反馈');
        return;
      }
      if (relayClientId) {
        await relayService.ensureConnected();
        const result = await withFeedbackTimeout(relayService.retryErrorReport(relayClientId, userError.errorId));
        if (result?.status) {
          if (result.status.state === 'reported') {
            confirmFeedback();
            return;
          }
          if (['failed', 'disabled', 'not_found'].includes(result.status.state)) {
            rejectFeedback(result.status.lastError ?? '反馈提交失败，请稍后重试');
            return;
          }
          if (result.status.state === 'pending') {
            setAwaitingCompletion(true);
            return;
          }
        }
        throw new Error('桌面客户端未返回异常反馈提交状态');
      } else {
        const result = await withFeedbackTimeout(window.electron.cowork.retryErrorReport(userError.errorId));
        if (result.status) {
          if (result.status.state === 'reported') {
            confirmFeedback();
            return;
          }
          if (['failed', 'disabled', 'not_found'].includes(result.status.state)) {
            rejectFeedback(result.status.lastError ?? '反馈提交失败，请稍后重试');
            return;
          }
          if (result.status.state === 'pending') {
            setAwaitingCompletion(true);
            return;
          }
        } else {
          throw new Error(result.error ?? '手工上报未能启动');
        }
        throw new Error('异常反馈提交状态无效');
      }
    } catch (error) {
      rejectFeedback(relayClientId
        ? '客户端离线，无法立即提交反馈'
        : error instanceof Error ? error.message : '反馈提交失败，请稍后重试');
    }
  };

  /** copyReference 将客服可检索的异常编号写入系统剪贴板。 */
  const copyReference = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(userError.supportReference);
      setReferenceCopied(true);
      if (copyResetTimer.current !== null) window.clearTimeout(copyResetTimer.current);
      copyResetTimer.current = window.setTimeout(() => setReferenceCopied(false), 1400);
    } catch {
      setReferenceCopied(false);
    }
  };

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 whitespace-nowrap">
      <div className="inline-flex min-w-0 items-center gap-1 opacity-60">
        <span title={userError.supportReference}>异常编号：{formatSupportReference(userError.supportReference)}</span>
        <button
          type="button"
          title={referenceCopied ? '已复制' : '复制异常编号'}
          aria-label={referenceCopied ? '异常编号已复制' : '复制异常编号'}
          onClick={() => { void copyReference(); }}
          className={`inline-flex h-5 w-5 items-center justify-center rounded transition-colors hover:bg-black/5 dark:hover:bg-white/10 ${referenceCopied ? 'text-emerald-600 opacity-100 dark:text-emerald-400' : 'opacity-65 hover:opacity-100'}`}
        >
          {referenceCopied ? (
            <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M16.704 5.293a1 1 0 0 1 .003 1.414l-7.25 7.28a1 1 0 0 1-1.416.002l-3.75-3.75a1 1 0 1 1 1.414-1.414l3.041 3.041 6.543-6.57a1 1 0 0 1 1.415-.003Z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <rect x="9" y="9" width="11" height="11" rx="2" />
              <path d="M15 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h3" />
            </svg>
          )}
        </button>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={submitting}
          aria-busy={submitting}
          aria-live="polite"
          onClick={() => { void submitFeedback(); }}
          title={feedbackError ?? '提交异常反馈'}
          style={!feedbackConfirmed && !feedbackError ? { color: 'var(--accent-color, #3B82F6)' } : undefined}
          className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${feedbackConfirmed
            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
            : feedbackError
              ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
              : 'hover:bg-black/5 dark:hover:bg-white/10'}`}
        >
          {submitting ? (
            <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" />
              <path className="opacity-75" fill="currentColor" d="M21 12a9 9 0 0 0-9-9v3a6 6 0 0 1 6 6h3Z" />
            </svg>
          ) : feedbackConfirmed ? (
            <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M16.704 5.293a1 1 0 0 1 .003 1.414l-7.25 7.28a1 1 0 0 1-1.416.002l-3.75-3.75a1 1 0 1 1 1.414-1.414l3.041 3.041 6.543-6.57a1 1 0 0 1 1.415-.003Z" clipRule="evenodd" />
            </svg>
          ) : null}
          <span>{submitting ? '提交中' : feedbackConfirmed ? '反馈已提交' : feedbackError ? '提交失败' : '提交反馈'}</span>
        </button>
      </div>
    </div>
  );
};

export default CoworkErrorReportControl;
